import React, { useEffect, useRef, useState } from "react";
import { format, parse } from "date-fns";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";

const parseApiDate = (value) => {
  if (!value) return null;
  const parsed = parse(value, "MM/dd/yyyy", new Date());
  if (!Number.isNaN(parsed.getTime())) return parsed;
  const iso = new Date(value);
  return Number.isNaN(iso.getTime()) ? null : iso;
};

const toApiDate = (date) => (date ? format(date, "MM/dd/yyyy") : "");

const isSameDay = (a, b) => {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
};

const isWithinRange = (date, start, end, excludeEnds = true) => {
  if (!date || !start || !end) return false;
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const startOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
  const endOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate()).getTime();
  const min = Math.min(startOnly, endOnly);
  const max = Math.max(startOnly, endOnly);
  if (dateOnly < min || dateOnly > max) return false;
  if (excludeEnds && (dateOnly === min || dateOnly === max)) return false;
  return true;
};

const filterLabelClass =
  "block text-[12px] font-medium text-[#6B6B6B] mb-1.5";
const filterInputClass =
  "h-11 w-full px-3 border border-[#DCDCDC] rounded-xl bg-white text-[13px] text-[#2F2F2F] outline-none transition-all hover:border-[#0B4D76]/40 focus:border-[#0B4D76] focus:ring-1 focus:ring-[#0B4D76]/20";

export default function DateRangeFilter({
  fromDate,
  toDate,
  onChange,
  className = "",
  label = "Date Range",
  buttonClassName,
}) {
  const datePickerRef = useRef(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const startParsed = parseApiDate(fromDate);
  const endParsed = parseApiDate(toDate);
  const initialStart = startParsed || new Date();
  const initialEnd = endParsed || initialStart;

  const [selectedRange, setSelectedRange] = useState({
    start: initialStart,
    end: initialEnd,
  });
  const [month, setMonth] = useState(initialStart.getMonth());
  const [year, setYear] = useState(initialStart.getFullYear());
  const [hoverDate, setHoverDate] = useState(null);

  useEffect(() => {
    if (datePickerOpen) return;
    const start = parseApiDate(fromDate);
    const end = parseApiDate(toDate);
    if (start && end) {
      setSelectedRange({ start, end });
      setMonth(start.getMonth());
      setYear(start.getFullYear());
    }
  }, [fromDate, toDate, datePickerOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target)) {
        setDatePickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const formatFilterDate = (value) => {
    const parsed = parseApiDate(value);
    if (!parsed) return "";
    return format(parsed, "dd-MM-yyyy");
  };

  const formatDateLabel = (date) => (date ? format(date, "dd-MM-yyyy") : "");

  const formatSelectedDateLabel = () => {
    const { start, end } = selectedRange;
    if (start && end) return `${formatDateLabel(start)} — ${formatDateLabel(end)}`;
    if (start) return formatDateLabel(start);
    return "Select date";
  };

  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const handlePrevMonth = () => {
    setMonth((prev) => {
      if (prev === 0) {
        setYear((y) => y - 1);
        return 11;
      }
      return prev - 1;
    });
  };

  const handleNextMonth = () => {
    setMonth((prev) => {
      if (prev === 11) {
        setYear((y) => y + 1);
        return 0;
      }
      return prev + 1;
    });
  };

  const handleDayClick = (day) => {
    const current = new Date(year, month, day);
    const { start, end } = selectedRange;

    // First click — start a new range
    if (!start || end) {
      setSelectedRange({ start: current, end: null });
      setHoverDate(null);
      return;
    }

    // Second click — complete the range
    const startDateOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const currentDateOnly = new Date(
      current.getFullYear(),
      current.getMonth(),
      current.getDate()
    );

    let startValue = startDateOnly;
    let endValue = currentDateOnly;
    if (currentDateOnly < startDateOnly) {
      startValue = currentDateOnly;
      endValue = startDateOnly;
    }

    setSelectedRange({ start: startValue, end: endValue });
    setHoverDate(null);
    onChange?.({
      fromDate: toApiDate(startValue),
      toDate: toApiDate(endValue),
    });
  };

  return (
    <div className={`relative ${className}`} ref={datePickerRef}>
      {label && <label className={filterLabelClass}>{label}</label>}
      <button
        type="button"
        onClick={() =>
          setDatePickerOpen((prev) => {
            if (!prev) {
              const base = parseApiDate(fromDate) || new Date();
              setMonth(base.getMonth());
              setYear(base.getFullYear());
            }
            return !prev;
          })
        }
        className={buttonClassName ?? `${filterInputClass} flex items-center justify-between gap-2 text-left`}
      >
        <span className="truncate text-[#2F2F2F]">
          {fromDate && toDate
            ? `${formatFilterDate(fromDate)} – ${formatFilterDate(toDate)}`
            : formatSelectedDateLabel()}
        </span>
        <Calendar size={15} className="text-[#0B4D76] shrink-0" />
      </button>

      {datePickerOpen ? (
        <div className="absolute top-full left-0 mt-2 z-30 w-[min(100vw-2rem,420px)] rounded-2xl border border-[#E8E8E8] bg-white p-4 shadow-xl">
          <div className="mb-3 flex items-center justify-between">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="h-9 w-9 rounded-full text-teal-600 hover:bg-teal-50 transition-all flex items-center justify-center"
            >
              <ChevronLeft size={20} />
            </button>
            <div className="text-[20px] font-semibold text-slate-800 leading-none">
              {format(new Date(year, month, 1), "MMMM yyyy")}
            </div>
            <button
              type="button"
              onClick={handleNextMonth}
              className="h-9 w-9 rounded-full text-teal-600 hover:bg-teal-50 transition-all flex items-center justify-center"
            >
              <ChevronRight size={20} />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-y-2 text-center text-[10px] font-black uppercase tracking-widest text-slate-300 mb-2">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
              <div key={day}>{day}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-y-1.5">
            {Array.from({ length: firstDayOfMonth }).map((_, idx) => (
              <div key={`empty-${idx}`} />
            ))}
            {Array.from({ length: daysInMonth }).map((_, idx) => {
              const day = idx + 1;
              const currentDate = new Date(year, month, day);
              const { start, end } = selectedRange;
              const previewEnd =
                end || (start && hoverDate && !end ? hoverDate : null);
              const isStart = isSameDay(currentDate, start);
              const isEnd = previewEnd && isSameDay(currentDate, previewEnd);
              const inRange = isWithinRange(currentDate, start, previewEnd);
              const isHoverEnd = !end && hoverDate && isSameDay(currentDate, hoverDate);

              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => handleDayClick(day)}
                  onMouseEnter={() => {
                    if (start && !end) setHoverDate(currentDate);
                  }}
                  onMouseLeave={() => setHoverDate(null)}
                  className={`mx-auto h-10 w-10 rounded-2xl text-[14px] leading-none font-medium transition-all flex items-center justify-center
                    ${
                      isStart || (isEnd && end)
                        ? "bg-teal-600 text-white shadow-lg shadow-teal-200"
                        : isHoverEnd
                          ? "bg-teal-100 text-teal-700 ring-2 ring-teal-300"
                          : inRange
                            ? "bg-teal-50 text-teal-700"
                            : "text-slate-700 hover:bg-slate-100"
                    }`}
                >
                  {day}
                </button>
              );
            })}
          </div>

          <div className="mt-5 flex items-center justify-between gap-2 border-t border-slate-100 pt-3">
            <span className="text-xs font-semibold text-slate-500 truncate">
              {formatSelectedDateLabel()}
            </span>
            <button
              type="button"
              onClick={() => setDatePickerOpen(false)}
              className="h-8 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-all"
            >
              Close
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
